import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Alert } from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { downloadBlob } from '../../../../../modules/refunds/utils/documentUtils';

interface GrouperHelpDialogProps {
  open: boolean;
  onClose: () => void;
}

const GrouperHelpDialog: React.FC<GrouperHelpDialogProps> = ({ open, onClose }) => {
  const handleDownloadTemplate = () => {
    const headers = ['Code', 'Rate', 'IsPerDiem'];
    const csvContent = headers.join(',');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    downloadBlob(blob, 'grouper_template.csv');
  };

  const csvColumns = [
    { field: 'Code', description: 'Grouper code identifier', example: 'A' },
    { field: 'Rate', description: 'Base rate for this grouper', example: '1500.00' },
    { field: 'IsPerDiem', description: 'Whether this is a per-diem rate', example: 'Y' },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Grouper/Base Rates Management Help</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box>
            <Typography variant="h6" gutterBottom>
              About Groupers/Base Rates
            </Typography>
            <Typography variant="body2" paragraph>
              Groupers define base rate categories used in contract reimbursement calculations. 
              Each grouper has a code identifier, a rate amount, and an optional per-diem indicator.
              CPT and DRG codes can reference these groupers to determine their reimbursement rates.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h6" gutterBottom>
              CSV Upload Format
            </Typography>
            <Typography variant="body2" paragraph>
              Upload a CSV file with the following columns. The first row should contain column headers:
            </Typography>
            
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Column Name</strong></TableCell>
                    <TableCell><strong>Description</strong></TableCell>
                    <TableCell><strong>Example</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {csvColumns.map((column) => (
                    <TableRow key={column.field}>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                        {column.field}
                      </TableCell>
                      <TableCell>{column.description}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                        {column.example}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Alert severity="info">
            <Typography variant="body2">
              <strong>Import Behavior:</strong> Uploading a CSV file will perform an upsert operation.
              Existing groupers with matching codes will be updated, and new groupers will be created.
              Groupers not included in the CSV file will remain unchanged.
            </Typography>
          </Alert>

          <Box>
            <Typography variant="h6" gutterBottom>
              Field Details
            </Typography>
            <Box sx={{ ml: 2 }}>
              <Typography variant="body2" paragraph>
                <strong>Code:</strong> A unique identifier for the grouper (e.g., A, B, C, or numeric codes like 001, 002).
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>Rate:</strong> The base rate amount as a decimal number (e.g., 1500.00, 2500.50).
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>IsPerDiem:</strong> Boolean value indicating if this is a per-diem rate. Accepts: Y, N, Yes, No, True, False.
              </Typography>
            </Box>
          </Box>

          <Box>
            <Typography variant="h6" gutterBottom>
              Example CSV Content
            </Typography>
            <Box sx={{ 
              bgcolor: 'grey.100', 
              p: 2, 
              borderRadius: 1, 
              fontFamily: 'monospace', 
              fontSize: '0.875rem',
              overflow: 'auto'
            }}>
              <pre>{`Code,Rate,IsPerDiem
A,1500.00,N
B,2000.00,N
C,750.00,Y
D,3500.00,N`}</pre>
            </Box>
          </Box>

          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadTemplate}
            sx={{ alignSelf: 'flex-start' }}
          >
            Download CSV Template
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GrouperHelpDialog;
